/**
 * Reaching a terminal on the wall by its number.
 *
 * A wall of six panes has one keyboard between them, and until now the only way
 * to move it was the pointer or a walk through the tab order — six Tab presses
 * past a grip, a menu and two buttons each. So the wall borrows the gesture the
 * thing it is made of has used for twenty years: **a prefix, then a digit**.
 * tmux is `Ctrl+B 3`, screen is `Ctrl+A 3`; here it is `Cmd+S 3`.
 *
 * **Why Cmd, when this console's rule is the function row.** The rule is that a
 * shortcut must use a key the shell and the browser have both left alone, and
 * until now that named the F-keys as the only such space. That was true of the *Ctrl* plane and is not true of Meta:
 * xterm sends nothing for a Cmd chord — it has no escape sequence to send — so
 * a pane cannot lose a keystroke to this. Ctrl could not be used here for a
 * reason worth writing down: `Ctrl+S` is XOFF, and binding it would freeze the
 * terminal of anyone who pressed it on a machine where flow control is on, with
 * no visible cause. The cost of Meta is that it is the Super key away from
 * macOS, where a window manager usually claims it — so this is an accelerator,
 * never the only way to do something. Every pane is still reachable by pointer
 * and by Tab.
 *
 * **The listener is on `window`, in the capture phase, and stops the event.**
 * That is what lets the chord work while the keyboard is inside a terminal:
 * capture runs before xterm's own handler on its textarea, so the digit is
 * consumed here and never reaches the shell. The console's other keys take the
 * opposite route — F1 and F2 are swallowed by the pane and bubble up — because
 * they are single keys with no state behind them; a chord has to be able to eat
 * the digit that answers it, and only the capture phase can.
 *
 * Inert outside a provider, exactly as the broadcast bus is: a lone pane pays
 * nothing and behaves as it always did.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { keyboardIsInAField } from '@/lib/keyboardOwner'

/** How long the prefix waits for its digit.
 *
 *  Bounded because an armed chord eats the next digit typed, and a prefix that
 *  stayed armed forever would do that to a keystroke pressed minutes later with
 *  no memory of having armed anything. Three seconds is long enough to look at
 *  the numbers on the headers and short enough that nobody is surprised. */
const ARM_MS = 3_000

/** The highest seat a digit can name. Ten panes on a wall is already past what
 *  fits on a screen, and there is no key for "10". */
const MAX_SEATS = 9

export interface PaneChord {
  /** This pane's seat, 1-9, or 0 for a pane past the ninth — and 0 outside a
   *  wall, which is the only "is this on" anything needs to ask. A separate
   *  `enabled` flag was written here and read by nothing; this codebase has
   *  shipped that shape at four layers already. */
  numberOf: (sessionId: string) => number
  /** True while the prefix is waiting for a digit, so the seats can say so. */
  armed: boolean
  /** A pane offers its keyboard for as long as it is mounted. */
  register: (sessionId: string, focus: () => void) => () => void
}

const inert: PaneChord = {
  numberOf: () => 0,
  armed: false,
  register: () => () => {},
}

const Ctx = createContext<PaneChord>(inert)

export function usePaneChord(): PaneChord {
  return useContext(Ctx)
}

export function PaneChordProvider({
  panes,
  onPick,
  children,
}: {
  /** The tiles the wall is drawing, in the order it draws them. Seat 1 is the
   *  first one on screen, which is the only numbering an operator can check
   *  without being told it. */
  panes: readonly string[]
  /** What the page does when a seat is chosen: the wall's own selection. The
   *  keyboard is moved from here; where the *selection* goes is the page's. */
  onPick: (sessionId: string) => void
  children: ReactNode
}) {
  const [armed, setArmed] = useState(false)
  // Refs, because the window listener is installed once and must not be torn
  // down and rebuilt on every render of a page that re-renders each second.
  const armedRef = useRef(false)
  const panesRef = useRef(panes)
  const pickRef = useRef(onPick)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Each pane's way of taking the keyboard, by session. A ref rather than
   *  state: registering must not re-render the wall. */
  const focusers = useRef(new Map<string, () => void>())

  panesRef.current = panes
  pickRef.current = onPick

  const disarm = useCallback(() => {
    armedRef.current = false
    setArmed(false)
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
  }, [])

  const arm = useCallback(() => {
    armedRef.current = true
    setArmed(true)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(disarm, ARM_MS)
  }, [disarm])

  const register = useCallback((sessionId: string, focus: () => void) => {
    focusers.current.set(sessionId, focus)
    return () => {
      // Only if it is still ours: a pane that remounted has already replaced
      // the entry, and a late cleanup would delete the live one.
      if (focusers.current.get(sessionId) === focus) focusers.current.delete(sessionId)
    }
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (armedRef.current) {
        // A modifier held down on its own is not an answer. Cmd is very likely
        // still down — the operator pressed Cmd+S and is reaching for the digit
        // — and treating its keydown as "something else" would cancel the chord
        // before the digit ever arrived.
        if (e.key === 'Meta' || e.key === 'Control' || e.key === 'Shift' || e.key === 'Alt') return
        if (/^[1-9]$/.test(e.key)) {
          e.preventDefault()
          e.stopPropagation()
          const id = panesRef.current[Number(e.key) - 1]
          disarm()
          if (!id) return
          pickRef.current(id)
          // The selection and the keyboard are two different things, and this
          // moves both: `onPick` makes the tile the page's active one, and the
          // pane's own focuser puts the caret in its terminal. Neither takes the
          // input lease — looking at a terminal must never take it from whoever
          // is typing (see TerminalSlot), which is why Cmd+I is a separate key.
          focusers.current.get(id)?.()
          return
        }
        if (e.key === 'Escape') {
          e.preventDefault()
          e.stopPropagation()
          disarm()
          return
        }
        // Anything else answers the chord with "not that": the prefix is spent,
        // and the key itself is let through to whatever it belongs to.
        disarm()
        return
      }
      if ((e.key === 's' || e.key === 'S') && e.metaKey && !e.ctrlKey && !e.altKey) {
        // Cmd+S is Save in every browser, and this is the one place the console
        // takes a key the browser has a use for. It is defensible only because
        // there is nothing on this page a browser save would produce — an SPA's
        // "Save page" writes a shell that cannot reconnect to anything.
        e.preventDefault()
        e.stopPropagation()
        // But not while somebody is writing.
        //
        // Cmd+S is muscle memory, and the hand that presses it in a prompt box
        // is not asking for a pane — it is asking to save the paragraph. Arming
        // there would put the chord in a state the operator does not know about
        // and then eat the next digit they typed, three seconds later, out of
        // the middle of a sentence. The keypress is still swallowed rather than
        // handed back to the browser: a save dialog over a live console is a
        // worse answer to that habit than no answer at all.
        //
        // A terminal is not "writing" for this purpose. xterm's own textarea is
        // where the keyboard sits whenever a pane has it, which is exactly when
        // the chord is most useful.
        if (!keyboardIsInAField()) arm()
      }
    }
    window.addEventListener('keydown', onKey, true)
    // A chord left armed while the window is not focused would fire on the
    // first digit typed after coming back, in whatever the operator was doing.
    const onBlur = () => disarm()
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener('blur', onBlur)
      if (timer.current) clearTimeout(timer.current)
    }
  }, [arm, disarm])

  const value = useMemo<PaneChord>(
    () => ({
      armed,
      register,
      numberOf: (sessionId: string) => {
        const at = panes.indexOf(sessionId)
        return at >= 0 && at < MAX_SEATS ? at + 1 : 0
      },
    }),
    [armed, panes, register],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
